import { MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { useDroppable } from "@dnd-kit/react";

import { Button } from "@/components/ui/button";
import type { BoardCard, BoardColumn, BoardMember } from "@/features/board/types";
import { TaskCard } from "@/features/board/TaskCard";
import { cn } from "@/lib/utils";

type KanbanColumnProps = {
  cards: BoardCard[];
  column: BoardColumn;
  members: BoardMember[];
  onAddCard: (columnId: string) => void;
  onDelete: (column: BoardColumn) => void;
  onOpenCard: (card: BoardCard) => void;
  onRename: (column: BoardColumn) => void;
};

export const KanbanColumn = ({
  cards,
  column,
  members,
  onAddCard,
  onDelete,
  onOpenCard,
  onRename,
}: KanbanColumnProps) => {
  const { ref, isDropTarget } = useDroppable({
    id: column.id,
    type: "column",
    accept: "card",
    collisionPriority: -1,
  });

  return (
    <section
      className={cn(
        "flex max-h-[calc(100dvh-13rem)] w-72 shrink-0 flex-col gap-2 rounded-xl bg-neutral-100 p-2 ring-1 ring-neutral-950/5 sm:w-80",
        isDropTarget && "bg-primary-soft/60 ring-primary/20"
      )}
      data-column-id={column.id}
    >
      <header className="flex items-center justify-between gap-2 px-1">
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 className="truncate text-base font-semibold text-neutral-950 sm:text-sm">{column.name}</h2>
          <p className="shrink-0 text-base tabular-nums text-neutral-500 sm:text-sm">{cards.length}</p>
        </div>
        <details className="group relative shrink-0">
          <summary className="relative grid size-8 cursor-pointer list-none place-items-center rounded-md text-neutral-500 hover:bg-white hover:text-neutral-950 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary [&::-webkit-details-marker]:hidden before:absolute before:top-1/2 before:left-1/2 before:size-[max(100%,3rem)] before:-translate-1/2 before:content-[''] pointer-fine:before:hidden">
            <span className="sr-only">Column actions</span>
            <MoreHorizontal className="size-4 shrink-0 stroke-current" />
          </summary>
          <div className="absolute top-9 right-0 z-20 w-40 rounded-lg bg-white p-1 shadow-lg ring-1 ring-neutral-950/10">
            <button
              className="flex w-full items-center gap-2 rounded-md py-2 pr-3 pl-2 text-left text-sm text-neutral-700 hover:bg-neutral-100"
              onClick={() => onRename(column)}
              type="button"
            >
              <Pencil className="size-4 shrink-0 stroke-current" />
              Rename
            </button>
            <button
              className="flex w-full items-center gap-2 rounded-md py-2 pr-3 pl-2 text-left text-sm text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={cards.length > 0}
              onClick={() => onDelete(column)}
              title={cards.length ? "Move every card before deleting this column" : undefined}
              type="button"
            >
              <Trash2 className="size-4 shrink-0 stroke-current" />
              Delete
            </button>
          </div>
        </details>
      </header>

      <ol
        className="scrollbar-subtle flex min-h-20 flex-1 flex-col gap-2 overflow-y-auto p-px"
        ref={ref}
        role="list"
      >
        {cards.map((card, index) => (
          <li key={card.id}>
            <TaskCard
              card={card}
              columnId={column.id}
              index={index}
              members={members}
              onOpen={onOpenCard}
            />
          </li>
        ))}
      </ol>

      <Button
        className="w-full justify-start pl-2 pr-3"
        onClick={() => onAddCard(column.id)}
        size="sm"
        type="button"
        variant="ghost"
      >
        <Plus className="size-4 shrink-0 stroke-current" />
        Add card
      </Button>
    </section>
  );
};
